import React from 'react';
import { colors } from '../styles/colors';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <div style={styles.container}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{...styles.button, ...(currentPage === 1 ? styles.disabled : {})}}
      >
        ‹
      </button>

      {getVisiblePages().map((page, index) => (
        <React.Fragment key={index}>
          {page === '...' ? (
            <span style={styles.dots}>...</span>
          ) : (
            <button
              onClick={() => onPageChange(page as number)}
              style={{
                ...styles.button,
                ...(currentPage === page ? styles.active : {})
              }}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{...styles.button, ...(currentPage === totalPages ? styles.disabled : {})}}
      >
        ›
      </button>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '2rem',
    padding: '1rem',
  },
  button: {
    padding: '0.5rem 0.75rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    minWidth: '40px',
    textAlign: 'center' as const,
  },
  active: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  dots: {
    padding: '0.5rem',
    color: colors.textLight,
  },
};

export default Pagination;